import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  IconButton,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useLocation, useNavigate } from "react-router-dom";

function UnrefinedUserStoriesPopup({
  open,
  onClose,
  unrefinedStories,
  enrichedStories,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const openUserStoriesImportedPage = () => {
    if (location.pathname === "/dashboard/generate-testcases/input") {
      navigate("/dashboard/generate-multi/testcases/output", {
        state: { userStories: [...enrichedStories, ...unrefinedStories] },
      });
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: "650px",
          maxHeight: "90vh",
          padding: "20px",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: "17px", fontWeight: "bold", fontFamily: "Manrope" }}
        >
          User Story Evaluation
        </Typography>
        <IconButton sx={{ color: "black" }} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>


      <DialogContent
        sx={{
          overflowY: "auto",
          maxHeight: "70vh",
          px: 0,
        }}
      >
        {/* Enriched Section */}
        {enrichedStories?.length > 0 && (
          <>
            <Typography
              sx={{
                fontSize: "14px",
                mb: 2,
                fontFamily: "Manrope",
                fontWeight: "bold"
              }}
            >
              The following user stories are ready for test design:
              <Divider />
            </Typography>


            {enrichedStories.map((story, idx) => (
              <Accordion
                key={`enriched-${idx}`}
                disableGutters
                sx={{ backgroundColor: "#F0F8FF", mb: 0 }}
              >
                <AccordionSummary expandIcon={<ArrowDropDownIcon sx={{ color: "black" }} />}>
                  <Typography sx={{ fontFamily: "Manrope", fontWeight: "bold" }}>
                    {story.Title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontSize: "14px", fontFamily: "Manrope" }}
                  >
                    Description:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: 1,
                      fontSize: "13px",
                      fontFamily: "Manrope",
                      mb:2
                    }}
                  >
                    {story.Description}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontSize: "14px", fontFamily: "Manrope" }}
                  >
                    Acceptance Criteria:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: 1,
                      fontSize: "13px",
                      fontFamily: "Manrope",
                      mb:2
                    }}
                  >
                    {Array.isArray(story["Acceptance Criteria"])
                      ? story["Acceptance Criteria"].map((item, index) => (
                        <React.Fragment key={index}>
                          {item}
                          <br />
                        </React.Fragment>
                      ))
                      : story["Acceptance Criteria"]
                        .toString()
                        .split(/\n|(?=\d+\.\s)/g) // ✅ Split on either \n or numbered points
                        .map((line, index) => (
                          <React.Fragment key={index}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontSize: "14px", fontFamily: "Manrope" }}
                  >
                    Additional Information:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: 1,
                      fontSize: "13px",
                      fontFamily: "Manrope",
                      mb:2
                    }}
                  >
                    {Array.isArray(story["Additional Information"])
                      ? story["Additional Information"].map((item, index) => (
                        <React.Fragment key={index}>
                          {item}
                          <br />
                        </React.Fragment>
                      ))
                      : story["Additional Information"]
                        .toString()
                        .split(/\n|(?=\d+\.\s)/g) // ✅ Split on either \n or numbered points
                        .map((line, index) => (
                          <React.Fragment key={index}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: "#000000",
                      fontFamily: "Manrope",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: "bold", fontFamily: "Manrope" }}
                    >
                      Reason:
                    </Box>{" "}
                    {story.reason}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
        {/* Unrefined Section */}
        {unrefinedStories?.length > 0 && (
          <>
            <Typography
              sx={{
                fontSize: "14px",
                mb: 2,
                fontFamily: "Manrope",
                fontWeight: "bold"
              }}
            >
              The following user stories are not ready for test design:
            </Typography>
            <Divider />

            {unrefinedStories.map((story, idx) => (
              <Accordion
                key={`unrefined-${idx}`}
                disableGutters
                sx={{
                  backgroundColor: "#EAF1FF",
                  mb: 0,
                  mt: 2,
                  fontFamily: "Manrope",
                }}
              >
                <AccordionSummary expandIcon={<ArrowDropDownIcon sx={{ color: "black" }} />}>
                  <Typography sx={{ fontFamily: "Manrope"}}>
                    {story.Title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontSize: "14px", fontFamily: "Manrope" }}
                  >
                    Description:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: 1,
                      fontSize: "13px",
                      fontFamily: "Manrope",
                      mb:2
                    }}
                  >
                    {story.Description}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontSize: "14px", fontFamily: "Manrope" }}
                  >
                    Acceptance Criteria:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: 1,
                      fontSize: "13px",
                      fontFamily: "Manrope",
                      mb:2
                    }}
                  >
                    {Array.isArray(story["Acceptance Criteria"])
                      ? story["Acceptance Criteria"].map((item, index) => (
                        <React.Fragment key={index}>
                          {item}
                          <br />
                        </React.Fragment>
                      ))
                      : story["Acceptance Criteria"]
                        .toString()
                        .split(/\n|(?=\d+\.\s)/g) // ✅ Split on either \n or numbered points
                        .map((line, index) => (
                          <React.Fragment key={index}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontSize: "14px", fontFamily: "Manrope" }}
                  >
                    Additional Information:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: 1,
                      fontSize: "13px",
                      fontFamily: "Manrope",
                      mb:2
                    }}
                  >
                    {Array.isArray(story["Additional Information"])
                      ? story["Additional Information"].map((item, index) => (
                        <React.Fragment key={index}>
                          {item}
                          <br />
                        </React.Fragment>
                      ))
                      : story["Additional Information"]
                        .toString()
                        .split(/\n|(?=\d+\.\s)/g) // ✅ Split on either \n or numbered points
                        .map((line, index) => (
                          <React.Fragment key={index}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#000000",
                      fontFamily: "Manrope",
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Reason:
                    </Box>{" "}
                    {story.reason}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
      </DialogContent>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Counts Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            flexWrap: "wrap",
            mt: 2,
            fontFamily: "Manrope",
          }}
        >
          <Typography
            sx={{ fontSize: "12px", fontFamily: "Manrope", fontWeight: "bold" }}
          >
            Total User Stories:{" "}
            {enrichedStories.length + unrefinedStories.length}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", fontFamily: "Manrope", fontWeight: "bold" }}
          >
            Refined User Stories: {enrichedStories.length}/
            {enrichedStories.length + unrefinedStories.length}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", fontFamily: "Manrope", fontWeight: "bold" }}
          >
            Need Refinement: {unrefinedStories.length}/
            {enrichedStories.length + unrefinedStories.length}
          </Typography>
        </Box>

        {/* Action Buttons Section (next line) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end", // <-- align to the left
            gap: 1,
            justifyContent: "flex-end",
            mb: 1,
          }}
        >
          {/* ✅ Case 1: All valid → Show Proceed */}
          {enrichedStories.length > 0 && unrefinedStories.length === 0 && (
            <Button
              onClick={openUserStoriesImportedPage}
              variant="contained"
              sx={{
                textTransform: "none",
                backgroundColor: "#49A3FF",
                fontFamily: "Manrope",
              }}
            >
              Proceed
            </Button>
          )}

          {/* ✅ Case 2: Some valid, some invalid → Show Back and Proceed Anyway link */}
          {enrichedStories.length > 0 && unrefinedStories.length > 0 && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={onClose}
                variant="contained"
                sx={{
                  textTransform: "none",
                  backgroundColor: "#49A3FF",
                  ml: 13,
                  fontFamily: "Manrope",
                }}
              >
                Prev
              </Button>
              <Button
                variant="contained"
                onClick={openUserStoriesImportedPage}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#49A3FF",
                  fontFamily: "Manrope",
                }}
              >
                Proceed
              </Button>
            </Box>
          )}

          {/* ✅ Case 3: All invalid → Show only Proceed Anyway link */}
          {enrichedStories.length === 0 && unrefinedStories.length > 0 && (
            <Button
              variant="contained"
              onClick={openUserStoriesImportedPage}
              sx={{
                textTransform: "none",
                backgroundColor: "#49A3FF",
                fontFamily: "Manrope",
              }}
            >
              Proceed
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default UnrefinedUserStoriesPopup;
